/** Formate de-DE (Abnahme-Checkliste 8): „12.09.2026“, „1.290,00 €“. */

const datumFormat = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
const monatFormat = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });
const betragFormat = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

/** ISO-Datum (JJJJ-MM-TT) ohne Zeitzonenverschiebung lesen. */
export function alsDatum(iso: string): Date {
  const [j, m, t] = iso.slice(0, 10).split("-").map(Number);
  return new Date(j ?? 1970, (m ?? 1) - 1, t ?? 1);
}

const berlinFormat = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" });

/** Kalendertag (JJJJ-MM-TT) eines Zeitpunkts in deutscher Zeit, wie ihn der Server rechnet: ein
 *  Brief, der am 1.1. um 00:30 eingeht, kommt als „…-12-31T23:30:00Z“ und gehoert zum 1.1. */
export function eingangstag(zeitpunkt: string): string {
  const d = new Date(zeitpunkt);
  return Number.isNaN(d.getTime()) ? zeitpunkt.slice(0, 10) : berlinFormat.format(d);
}

/** Dokumentart: der Server nimmt nach dem Trimmen hoechstens 60 Zeichen (`schemas/ki.py` Art). */
export const DOKUMENTART_MAX = 60;

/** Laenge wie der Server zaehlt (Python `len`): Codepunkte, nicht UTF-16-Einheiten (`String.length`,
 *  natives maxLength) und nicht Grapheme. Ein Familien-Emoji sind 7 Zeichen, „😀“ eines. */
export function zeichen(text: string): number {
  return Array.from(text).length;
}

/** Eingabe auf hoechstens `max` Codepunkte kuerzen, gezaehlt nach dem Trimmen wie der Server. */
export function hoechstens(text: string, max: number): string {
  return zeichen(text.trim()) > max ? Array.from(text.trimStart()).slice(0, max).join("") : text;
}

/** „12.01.2026“ aus „2026-01-12“. Wirft nie: einen Wert, der kein Tag ist („12.01.2026“, „20260112“),
 *  gibt sie roh zurueck, statt die Seite abstuerzen zu lassen (W37c, Verifier R2 A1). */
export function datum(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = alsDatum(iso);
  return Number.isNaN(d.getTime()) ? iso : datumFormat.format(d);
}

/** Datumszeile eines Briefs (D-073, Pruefkarte, Dokumentzeile, Suche): „Brief vom 04.10.2026“ aus dem
 *  Briefdatum; nur ohne erkanntes Briefdatum „Eingang 06.10.2026“, der Tag des Hochladens. */
export function datumsZeile(d: { datum: string | null; erstellt: string }): string {
  // „Brief vom“ mit geschuetztem Leerzeichen: neben einer Frist bricht nur das Datum um (W22d-w8b, Verifier w8 H2)
  return d.datum ? `Brief\u00a0vom ${datum(d.datum)}` : `Eingang ${datum(eingangstag(d.erstellt))}`;
}

/** Datum fuer Screenreader (D-073): „Brief vom 04.10.2026“, ohne Briefdatum „eingegangen am 06.10.2026“. */
export function datumGesprochen(d: { datum: string | null; erstellt: string }): string {
  return d.datum ? `Brief vom ${datum(d.datum)}` : `eingegangen am ${datum(eingangstag(d.erstellt))}`;
}

/** Eingang eines Briefs als eigene Zeile der Dokumentansicht (D-073): „Eingegangen am 06.10.2026“. */
export function eingangsZeile(erstellt: string): string {
  return `Eingegangen am ${datum(eingangstag(erstellt))}`;
}

const zeitFormat = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const zahlFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });

/** Zeitpunkt mit Uhrzeit in der Zeitzone des Geraets („30.09.2026, 14:05“). */
export function zeitpunkt(iso: string | null | undefined): string {
  return iso ? zeitFormat.format(new Date(iso)) : "";
}

const uhrzeitFormat = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" });

/** Nur die Uhrzeit in der Zeitzone des Geraets („14:05“). */
export function uhrzeit(iso: string | null | undefined): string {
  return iso ? uhrzeitFormat.format(new Date(iso)) : "";
}

/** Dateigroesse („350 KB“, „12,4 MB“, „1,2 GB“), Basis 1024 wie der Server. */
export function groesse(bytes: number): string {
  const einheiten = ["Byte", "KB", "MB", "GB"];
  let wert = bytes;
  let i = 0;
  while (wert >= 1024 && i < einheiten.length - 1) {
    wert /= 1024;
    i += 1;
  }
  return `${zahlFormat.format(wert)} ${einheiten[i]}`;
}

export function monat(iso: string): string {
  return monatFormat.format(alsDatum(iso));
}

export function betrag(wert: string | number | null | undefined): string {
  if (wert === null || wert === undefined || wert === "") return "";
  return betragFormat.format(Number(wert));
}

/** Betrag fuer Screenreader („1.290,00 Euro“). */
export function betragGesprochen(wert: string | null): string {
  return wert ? betrag(wert).replace("€", "Euro").trim() : "";
}

export function heute(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Ganze Tage von heute bis zum Datum (negativ = vorbei). */
export function tageBis(iso: string, jetzt: Date = heute()): number {
  return Math.round((alsDatum(iso).getTime() - jetzt.getTime()) / 86_400_000);
}

export type Fristzustand = "ueberfaellig" | "bald" | "spaeter" | "erledigt";

/** Frist als kurzes Wort statt Chip (Posteingang, W22b): „seit 3 Tagen“ (ueberfaellig), „heute“,
 *  „morgen“, „in 2 Tagen“, sonst „bis 18.10.2026“. */
export function fristKurz(iso: string, jetzt: Date = heute()): { text: string; ueberfaellig: boolean } {
  const tage = tageBis(iso, jetzt);
  if (tage < 0) return { text: tage === -1 ? "seit 1 Tag" : `seit ${-tage} Tagen`, ueberfaellig: true };
  if (tage === 0) return { text: "heute", ueberfaellig: false };
  if (tage === 1) return { text: "morgen", ueberfaellig: false };
  if (tage <= 7) return { text: `in ${tage} Tagen`, ueberfaellig: false };
  return { text: `bis ${datum(iso)}`, ueberfaellig: false };
}

/** Badge-Zustand und Text nach Designsprache-Tokens 8.9. */
export function fristText(iso: string, erledigt: boolean, jetzt: Date = heute()): { zustand: Fristzustand; text: string } {
  if (erledigt) return { zustand: "erledigt", text: "Erledigt" };
  const tage = tageBis(iso, jetzt);
  if (tage < 0) return { zustand: "ueberfaellig", text: tage === -1 ? "1 Tag überfällig" : `${-tage} Tage überfällig` };
  if (tage === 0) return { zustand: "bald", text: "Heute" };
  if (tage === 1) return { zustand: "bald", text: "Morgen" };
  if (tage <= 7) return { zustand: "bald", text: `in ${tage} Tagen` };
  return { zustand: "spaeter", text: `bis ${datum(iso)}` };
}

export const FRISTARTEN: Record<string, string> = {
  zahlen: "Zahlen",
  widersprechen: "Widersprechen",
  kuendigen: "Kündigen",
  melden: "Melden",
  sonstige: "Frist",
};

export const TURNUS: Record<string, string> = {
  monatlich: "monatlich",
  vierteljaehrlich: "vierteljährlich",
  halbjaehrlich: "halbjährlich",
  jaehrlich: "jährlich",
  einmalig: "einmalig",
};

/** W47e: Turnus einer Rate am Brief als Nachsatz („21,05 € je Monat“); `TURNUS` oben sind die Adjektive der Akte. */
export const TURNUS_JE: Record<string, string> = {
  monatlich: "je Monat",
  vierteljaehrlich: "je Vierteljahr",
  halbjaehrlich: "je Halbjahr",
  jaehrlich: "je Jahr",
};

/** W47e: Rate als Text, nur mit Betrag und bekanntem Turnus, sonst null. Ueber `betrag()`, nie ueber einen
 *  Eingabe-Parser (Plan W4). */
export function rateText(wert: string | null | undefined, turnus: string | null | undefined): string | null {
  const je = turnus && Object.hasOwn(TURNUS_JE, turnus) ? TURNUS_JE[turnus] : undefined;
  if (!wert || !je) return null;
  return `${betrag(wert)} ${je}`;
}

export const AKTENARTEN: Record<string, string> = {
  vertrag: "Vertrag",
  versicherung: "Versicherung",
  forderung: "Forderung",
  sonstige: "Sonstiges",
};

/** Fundstellen der Suche (⟦…⟧) als Textteile; nie als HTML einsetzen (Text aus dem OCR). */
export function fundstellen(kontext: string): { text: string; treffer: boolean }[] {
  const teile: { text: string; treffer: boolean }[] = [];
  const muster = /⟦([^⟧]*)⟧/g;
  let ab = 0;
  for (let m = muster.exec(kontext); m; m = muster.exec(kontext)) {
    if (m.index > ab) teile.push({ text: kontext.slice(ab, m.index), treffer: false });
    teile.push({ text: m[1] ?? "", treffer: true });
    ab = m.index + m[0].length;
  }
  if (ab < kontext.length) teile.push({ text: kontext.slice(ab), treffer: false });
  return teile;
}

/** Betragseingabe in der API-Form „1290.50“; leer ist null, Unlesbares ebenfalls null (dann
 *  meldet das Feld den Fehler). „1.290,50“, „1290,5“, „1290.50“ und „1.290“ sind erlaubt: mit Komma
 *  sind Punkte Tausender, ohne Komma ist ein Punkt vor genau drei Ziffern ein Tausender. W82 Web-10c (W10C-3, PA:19, F17,
 *  wie iOS `Modelle+Akten.swift`): nur wenn die erste Gruppe nicht mit 0 beginnt, „0.500“ ist kein Betrag. */
export function betragEingabe(text: string): string | null {
  let t = text.replace(/[\s€]/g, "");
  if (!t) return null;
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  else if (/^[1-9]\d{0,2}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Number(t).toFixed(2);
}
